import { useEffect, useState, useRef } from 'react';
import {
    WILDCARD_ROUND,
    FINALS_WEEK_ONE_ROUND,
    SEMI_FINALS_ROUND,
    PRELIMINARY_FINALS_ROUND,
    GRAND_FINAL_ROUND
} from '../utils/generate-bracket';

const SCROLL_TOS_INITIAL_STATE = [
    { round: WILDCARD_ROUND, willScroll: false, hasScrolled: false },
    { round: FINALS_WEEK_ONE_ROUND, willScroll: false, hasScrolled: false },
    { round: SEMI_FINALS_ROUND, willScroll: false, hasScrolled: false },
    {
        round: PRELIMINARY_FINALS_ROUND,
        willScroll: false,
        hasScrolled: false
    },
    { round: GRAND_FINAL_ROUND, willScroll: false, hasScrolled: false }
];

/**
 * Custom hook to handle auto-scrolling logic for the finals brackets in the ladder predictor.
 *
 * @param qualifyingFinalsMatches.qualifyingFinalsMatches
 * @param {Array} qualifyingFinalsMatches - Matches for qualifying finals round
 * @param {Array} semiFinalsMatches - Matches for semi finals round
 * @param {Array} preliminaryFinalsMatches - Matches for preliminary finals round
 * @param {Array} predictions - User's current predictions
 * @param qualifyingFinalsMatches.semiFinalsMatches
 * @param qualifyingFinalsMatches.preliminaryFinalsMatches
 * @param qualifyingFinalsMatches.grandFinal
 * @param qualifyingFinalsMatches.predictions
 */
export const useAutoScroll = ({
    qualifyingFinalsMatches,
    semiFinalsMatches,
    preliminaryFinalsMatches,
    grandFinal,
    predictions
}) => {
    const [scrollTos, setScrollTos] = useState(SCROLL_TOS_INITIAL_STATE);

    const containerRef = useRef(null);
    const bracketRefs = useRef([]);

    const addToRefs = (el) => {
        if (el && !bracketRefs.current.includes(el)) {
            bracketRefs.current.push(el);
        }
    };

    const getBracketXPositions = () => {
        return bracketRefs.current.map((ref) => ({
            el: ref,
            xPos: ref.offsetLeft,
            width: ref.offsetWidth
        }));
    };

    const scrollToRound = (xPos, width) => {
        if (!containerRef.current) {
            return;
        }

        const containerWidth = containerRef.current.clientWidth;
        const centeredPosition = xPos - containerWidth / 2 + width / 2;

        containerRef.current.scrollTo({
            left: Math.max(0, centeredPosition),
            behavior: 'smooth'
        });
    };

    const isRoundCompleted = (matches) => {
        if (!matches?.length) {
            return false;
        }
        return matches.every((match) => match.home.team && match.away.team);
    };

    // Check which rounds are completed and mark them for scrolling
    useEffect(() => {
        if (qualifyingFinalsMatches?.length) {
            if (isRoundCompleted(qualifyingFinalsMatches)) {
                setScrollTos((prev) =>
                    prev.map((item) =>
                        item.round === FINALS_WEEK_ONE_ROUND
                            ? { ...item, willScroll: true }
                            : item
                    )
                );
            }
        }

        if (semiFinalsMatches?.length) {
            if (isRoundCompleted(semiFinalsMatches)) {
                setScrollTos((prev) =>
                    prev.map((item) =>
                        item.round === SEMI_FINALS_ROUND
                            ? { ...item, willScroll: true }
                            : item
                    )
                );
            }
        }

        if (preliminaryFinalsMatches?.length) {
            if (isRoundCompleted(preliminaryFinalsMatches)) {
                setScrollTos((prev) =>
                    prev.map((item) =>
                        item.round === PRELIMINARY_FINALS_ROUND
                            ? { ...item, willScroll: true }
                            : item
                    )
                );
            }
        }

        if (grandFinal) {
            if (grandFinal.home?.team && grandFinal.away?.team) {
                setScrollTos((prev) =>
                    prev.map((item) =>
                        item.round === GRAND_FINAL_ROUND
                            ? { ...item, willScroll: true }
                            : item
                    )
                );
            }
        }
    }, [
        qualifyingFinalsMatches,
        semiFinalsMatches,
        preliminaryFinalsMatches,
        grandFinal
    ]);

    useEffect(() => {
        if (!containerRef.current || bracketRefs.current.length === 0) {
            return;
        }

        const observer = new ResizeObserver(() => {
            // Only trigger once we have actual widths
            const wildcard = bracketRefs.current.find(
                (ref) => Number.parseInt(ref.dataset.round) === WILDCARD_ROUND
            );

            if (wildcard && wildcard.offsetWidth > 0) {
                scrollToRound(wildcard.offsetLeft, wildcard.offsetWidth);
                observer.disconnect();
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Execute the scroll when willScroll is true
    useEffect(() => {
        if (scrollTos.some((item) => item.willScroll && !item.hasScrolled)) {
            // Use requestAnimationFrame to ensure DOM has fully rendered
            const animationFrameId = requestAnimationFrame(() => {
                const bracketXPositions = getBracketXPositions();

                scrollTos.forEach((item) => {
                    if (item.willScroll && !item.hasScrolled) {
                        const roundToScroll = bracketXPositions.find(
                            (round) =>
                                Number.parseInt(round.el.dataset.round) ===
                                item.round
                        );

                        if (roundToScroll) {
                            scrollToRound(
                                roundToScroll.xPos,
                                roundToScroll.width
                            );

                            setScrollTos((prev) =>
                                prev.map((scrollItem) =>
                                    scrollItem.round === item.round
                                        ? { ...scrollItem, hasScrolled: true }
                                        : scrollItem
                                )
                            );
                        }
                    }
                });
            });

            return () => cancelAnimationFrame(animationFrameId);
        }
    }, [scrollTos]);

    // Reset scroll state when predictions are cleared
    useEffect(() => {
        if (predictions.length === 0) {
            const animationFrameId = requestAnimationFrame(() => {
                // Use double requestAnimationFrame to ensure proper timing on view transitions
                requestAnimationFrame(() => {
                    const bracketXPositions = getBracketXPositions();
                    const wildcardRef = bracketXPositions.find(
                        (item) =>
                            Number.parseInt(item.el.dataset.round) ===
                            WILDCARD_ROUND
                    );

                    if (wildcardRef) {
                        scrollToRound(wildcardRef.xPos, wildcardRef.width);
                    } else if (bracketXPositions.length > 0) {
                        // Scroll to first bracket if wildcard round not found
                        const firstBracket = bracketXPositions[0];
                        scrollToRound(firstBracket.xPos, firstBracket.width);
                    }
                });

                setScrollTos(SCROLL_TOS_INITIAL_STATE);
            });

            return () => cancelAnimationFrame(animationFrameId);
        }
    }, [predictions]);

    return {
        containerRef,
        addToRefs,
        scrollTos
    };
};
